import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const CATEGORIES = ["Restaurant", "Retail", "Grocery", "Health", "Finance", "Technology", "Beauty", "Services", "Manufacturing", "Real Estate", "Other"];

export default function SubmitBusiness() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", category: "Restaurant", description: "", address: "", city: "", state: "",
    website: "", phone: "", email: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/businesses", form);
      toast.success("Business submitted for review. An admin will approve it shortly.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grain min-h-screen max-w-3xl mx-auto px-6 sm:px-10 py-20" data-testid="submit-business-page">
      <div className="label-eyebrow">Grow The Directory</div>
      <h1 className="mt-4 font-serif text-5xl text-bdu-cream tracking-tight">Submit a Black-Owned Business</h1>
      <p className="mt-4 text-bdu-cream/60 max-w-2xl">
        Every submission is reviewed by our admin team before going live. Owners must be Black-American,
        and businesses must actively serve their community.
      </p>

      <form onSubmit={onSubmit} className="mt-12 space-y-6 bdu-card p-8 lg:p-10">
        <Field label="Business Name">
          <input required value={form.name} onChange={update("name")} className="bdu-input" data-testid="business-name-input" />
        </Field>
        <Field label="Category">
          <select value={form.category} onChange={update("category")} className="bdu-input" data-testid="business-category-input">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Description">
          <textarea required minLength={10} rows={4} value={form.description} onChange={update("description")} className="bdu-input" data-testid="business-description-input" />
        </Field>
        <Field label="Street Address (optional)">
          <input value={form.address} onChange={update("address")} className="bdu-input" data-testid="business-address-input" placeholder="24490 Stone Mountain Lithonia Rd Ste E" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="City"><input required value={form.city} onChange={update("city")} className="bdu-input" data-testid="business-city-input" /></Field>
          <Field label="State"><input required value={form.state} onChange={update("state")} className="bdu-input" data-testid="business-state-input" /></Field>
        </div>
        <Field label="Website (optional)"><input type="url" value={form.website} onChange={update("website")} className="bdu-input" data-testid="business-website-input" placeholder="https://" /></Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Phone (optional)"><input value={form.phone} onChange={update("phone")} className="bdu-input" data-testid="business-phone-input" /></Field>
          <Field label="Contact Email (optional)"><input type="email" value={form.email} onChange={update("email")} className="bdu-input" data-testid="business-email-input" /></Field>
        </div>
        <button disabled={submitting} className="btn-gold w-full justify-center" type="submit" data-testid="submit-business-btn">
          {submitting ? "Submitting..." : "Submit for Review"}
        </button>
      </form>

      <style>{`
        .bdu-input {
          width: 100%;
          background: transparent;
          border: 1px solid rgba(212,175,55,0.2);
          padding: 0.75rem 1rem;
          color: #FDFBF7;
          outline: none;
          transition: border-color 0.2s ease-out;
          font-family: Manrope, sans-serif;
          font-size: 0.9rem;
        }
        .bdu-input:focus { border-color: #D4AF37; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-3">{label}</div>
      {children}
    </label>
  );
}
